import { useState } from "react";
import { Sparkles } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { Textarea } from "@/components/ui/Select";
import { useToast } from "@/components/ui/Toast";
import { useUserTimeZone } from "@/utils/datetime";
import { useOptionalAuth } from "@/hooks/useAuth";
import { extractErrorMessage } from "@/api/client";
import { leadsApi } from "@/api/leads";
import { leadKeys } from "@/hooks/leads/leadKeys";
import type {
  CreateAppointmentDto,
  ParsedAppointmentWithClassificationDto,
} from "@/api/appointments";
import { useAiParseAppointment, useCreateAppointment } from "../hooks/useAppointments";
import { AppointmentFormModal } from "./AppointmentFormModal";
import { LeadSuggestionSection, type LeadFormState } from "./LeadSuggestionSection";

interface Props {
  open: boolean;
  onClose: () => void;
}

const EMPTY_LEAD: LeadFormState = {
  firstName: "",
  lastName: "",
  companyName: "",
  title: "",
  phone: "",
  email: "",
  city: "",
  state: "",
  notes: "",
};

/**
 * Calendar AI Create+ — two-step flow. Step 1: paste text and "Parse with AI".
 * Step 2: the standard appointment form, pre-filled from the parse, optionally
 * with an "Also create a Lead" section. The Create button orchestrates the
 * two-call submit (POST /leads → POST /appointments linked to the lead).
 */
export function AiCreateAppointmentPanel({ open, onClose }: Props) {
  const tz = useUserTimeZone();
  const { toast } = useToast();
  const auth = useOptionalAuth();
  const qc = useQueryClient();
  const parse = useAiParseAppointment();
  const create = useCreateAppointment();

  const [rawText, setRawText] = useState("");
  // The parse result plus the text it came from. `pastedSource` is CLIENT-ONLY
  // — it rides the prefill object into the review form and is composed into the
  // Description there; it is never sent to the server as a field of its own.
  const [parsed, setParsed] = useState<
    (ParsedAppointmentWithClassificationDto & { pastedSource?: string }) | null
  >(null);
  const [createLead, setCreateLead] = useState(false);
  const [leadForm, setLeadForm] = useState<LeadFormState>(EMPTY_LEAD);

  const canCreateLead = auth?.hasPermission("Leads.Create") ?? false;

  const close = () => {
    setRawText("");
    setParsed(null);
    setCreateLead(false);
    setLeadForm(EMPTY_LEAD);
    onClose();
  };

  const runParse = async () => {
    const text = rawText.trim();
    if (!text) return;
    try {
      const result = await parse.mutateAsync({ rawText: text, timezone: tz });
      const lead = result.lead;
      setCreateLead(false);
      setLeadForm(
        lead
          ? {
              firstName: lead.firstName ?? "",
              lastName: lead.lastName ?? "",
              companyName: lead.companyName ?? "",
              title: lead.title ?? "",
              phone: lead.phone ?? "",
              email: lead.email ?? "",
              city: lead.city ?? "",
              state: lead.state ?? "",
              notes: lead.notes ?? "",
            }
          : EMPTY_LEAD,
      );
      // Composed here, not in the JSX below: `prefill` is in the review form's
      // hydration-effect dependency array, so an object literal built during
      // render would take a fresh identity every render and overwrite every
      // field the user has typed.
      setParsed({ ...result, pastedSource: text });
    } catch {
      // Surfaced inline via parse.isError below.
    }
  };

  const handleSubmit = async (payload: CreateAppointmentDto) => {
    let relatedEntityType = payload.relatedEntityType;
    let relatedEntityId = payload.relatedEntityId;
    let leadCreated = false;

    if (createLead && canCreateLead) {
      try {
        const lead = await leadsApi.create({
          firstName: leadForm.firstName.trim(),
          lastName: leadForm.lastName.trim(),
          companyName: leadForm.companyName.trim() || null,
          title: leadForm.title.trim() || null,
          phone: leadForm.phone.trim() || null,
          email: leadForm.email.trim() || null,
          city: leadForm.city.trim() || null,
          state: leadForm.state.trim() || null,
          notes: leadForm.notes.trim() || null,
          nextFollowUpDate: payload.startAt,
          source: "Other",
          interestLevel: "Unknown",
        });
        leadCreated = true;
        relatedEntityType = "Lead";
        relatedEntityId = lead.id;
        void qc.invalidateQueries({ queryKey: leadKeys.all });
      } catch (err) {
        toast({
          message: `Couldn't create the lead: ${extractErrorMessage(err)}`,
          tone: "error",
        });
        return; // Don't create the appointment if the lead failed.
      }
    }

    try {
      await create.mutateAsync({ ...payload, relatedEntityType, relatedEntityId });
      toast({
        message: leadCreated ? "Appointment and lead created." : "Appointment created.",
        tone: "success",
      });
      close();
    } catch (err) {
      toast({
        message: leadCreated
          ? `The lead was created — find it in the Leads list. Appointment failed: ${extractErrorMessage(err)}`
          : extractErrorMessage(err),
        tone: "error",
      });
      // Leave the panel open so the appointment can be retried.
    }
  };

  // Step 2 — review: reuse the appointment form, pre-filled.
  if (parsed) {
    return (
      <AppointmentFormModal
        open={open}
        onClose={close}
        prefill={parsed}
        onBack={() => setParsed(null)}
        onSubmitOverride={handleSubmit}
        extraContent={
          parsed.lead && canCreateLead ? (
            <LeadSuggestionSection
              enabled={createLead}
              onEnabledChange={setCreateLead}
              form={leadForm}
              onFormChange={setLeadForm}
            />
          ) : null
        }
      />
    );
  }

  // Step 1 — paste.
  return (
    <Modal
      open={open}
      onClose={close}
      dismissOnBackdropClick={false}
      title="AI Create+"
      description="Paste an email, meeting notes, or any text and we'll draft the appointment."
      size="md"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={close}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={runParse}
            loading={parse.isPending}
            disabled={!rawText.trim()}
          >
            <Sparkles size={16} strokeWidth={2} aria-hidden />
            Parse with AI
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-2">
        <Textarea
          rows={8}
          value={rawText}
          onChange={(e) => setRawText(e.target.value)}
          placeholder="Paste an email, meeting notes, or any text…"
          aria-label="Text to parse"
        />
        {parse.isError && (
          <p className="m-0 text-label text-status-error" role="alert">
            {extractErrorMessage(parse.error)}
          </p>
        )}
      </div>
    </Modal>
  );
}
